import { GetServerSidePropsContext } from 'next';
import { NextSeo } from 'next-seo';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import fetchByProperties from '~/api/utils';
import { ICreatorPlatformStatusValue } from '~/clientApi';
import Error404 from '~/components/Error404';
import Error500 from '~/components/Error500';
import { MarketingBanner } from '~/components/MarketingBanner';
import { SHARED_ENTITIES } from '~/components/MarketingBanner/MarketingBanner';
import { FEATURE_FLAGS, SOCIAL_PLATFORMS } from '~/constants';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { processOgImages } from '~/metaDataUtils';
import { ContentSharedScreen } from '~/screens/ContentSharedScreen';
import { InstagramContentType } from '~/screens/ContentSharedScreen/ContentSharedScreen.types';
import { IApiSearchInstagramContent } from '~/searchApi/content/searchContentApi.model';
import { CreatorType } from '~/types';

interface Props {
  contentServer: InstagramContentType;
  creatorServer: CreatorType;
}

export default function SharedLiveContentDetailPageBrand({
  contentServer,
  creatorServer,
}: Props) {
  const isMetricsCollaborationFFEnbled = useFeature(
    FEATURE_FLAGS.foamMetricsCollaborations,
  ).isEnabled;
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const content: IApiSearchInstagramContent = {
    ...contentServer,
    platform: SOCIAL_PLATFORMS.instagram,
    publishedAt: contentServer.postedAt,
    contentId: contentServer.id,
    createdTime: contentServer.createdAt,
    creator: {
      id: creatorServer.id,
      platforms: {},
      status: creatorServer.status as ICreatorPlatformStatusValue,
    },
    views: contentServer.plays,
    viewsCount: contentServer.views || contentServer.plays,
    shareUrl: contentServer.permalink,
    status: contentServer.status,
  };

  const handleError = ({ error }: { error: Error }) => {
    if (error.message.match(/404/)) {
      return <Error404 />;
    }
    return <Error500 />;
  };

  return (
    <ErrorBoundary fallbackRender={handleError}>
      <NextSeo
        title="Share Live Content"
        openGraph={{
          title: creatorServer.fullName,
          description: `IG ${contentServer.channel}`,
          type: 'website',
          images: processOgImages(
            [contentServer.assets[0].previewUrl as string],
            'content',
          ),
        }}
      />
      {isClient && (
        <>
          <ContentSharedScreen
            // shareLink not available through the API in share version, so we use current URL
            content={{ ...content, shareLink: window.location.href }}
            creator={{
              fullName: creatorServer.fullName as string,
              avatar: creatorServer.avatar as string,
              slug: creatorServer.slug as string,
              status: creatorServer.status as ICreatorPlatformStatusValue,
            }}
            isMetricsCollaborationFFEnbled={isMetricsCollaborationFFEnbled}
          />
          <MarketingBanner
            hide={!creatorServer?.agency}
            agencyName={creatorServer?.agency?.name}
            entity={SHARED_ENTITIES.content}
            trackId="foam-marketing-banner"
          />
        </>
      )}
    </ErrorBoundary>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { sharedToken } = context.params as { sharedToken: string };
  const content = await fetchByProperties('contents', sharedToken, []);
  const creator = await fetchByProperties(
    `creators/${content.results[0].creator.id}`,
    sharedToken,
    ['fullName', 'avatar', 'slug', 'agency'],
  );

  return {
    props: {
      isSSR: true,
      contentServer: content.results[0],
      creatorServer: creator,
    },
  };
}
